import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDexExecCost, type MandateParams } from "../api/client.ts";

// Sibling to LpMarkoutCard — same palette/visual weight. Reference-relative
// taker execution cost = -1 * LP markout @ t0 (one source of truth). Positive =
// taker paid ABOVE external reference (normal cost); negative = filled better
// than reference (e.g. venue trades rich vs the CEX reference on that flow).
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

const POLL_MS = 30_000;

function useDexExecCost(p: MandateParams) {
  return useQuery({
    queryKey: ["dex-exec-cost", p.clientId, p.venue, p.pair],
    queryFn: () => fetchDexExecCost(p),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });
}

function fmtSigned(n: number | null | undefined, d = 1): string {
  if (n == null) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(d)}`;
}
function fmtUsd(n: number | null | undefined): string {
  if (n == null) return "—";
  const a = Math.abs(n);
  const s = a >= 1000 ? `$${(a / 1000).toFixed(1)}k` : `$${a.toFixed(0)}`;
  return n < 0 ? `−${s}` : s;
}
// Positive cost (taker paid above reference) = amber; <=0 (filled at/better) = green.
function costColor(n: number | null | undefined): string {
  if (n == null) return MUTED;
  return n > 0 ? AMBER : GREEN;
}

const cardStyle = {
  background: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "12px 14px",
} as const;
const headRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 8,
} as const;

export function ExecCostCard({
  venue,
  pair,
  clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useDexExecCost({ venue, pair, clientId });
  const m = q.data;
  const priced = m?.n_ok ?? 0;

  let body: ReactNode;
  if (!m || priced === 0 || m.economic_taker_cost_bps == null) {
    body = (
      <>
        <div style={{ fontSize: 24, fontWeight: 500, color: MUTED }}>—</div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>
          {q.isError ? "Could not load." : "No priced swaps vs the external reference in the window."}
        </div>
      </>
    );
  } else {
    body = (
      <>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 500,
              color: costColor(m.economic_taker_cost_bps),
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtSigned(m.economic_taker_cost_bps)}
          </span>
          <span style={{ fontSize: 12, color: MUTED }}>bps size-weighted vs reference</span>
        </div>

        <div style={{ fontSize: 12, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>
          typical fill{" "}
          <span style={{ color: costColor(m.median_taker_cost_bps), fontVariantNumeric: "tabular-nums" }}>
            {fmtSigned(m.median_taker_cost_bps)}
          </span>{" "}
          bps (median per fill) · cumulative{" "}
          <span style={{ color: TEXT, fontVariantNumeric: "tabular-nums" }}>
            {fmtUsd(m.taker_cost_usd)}
          </span>
        </div>

        <div
          style={{
            fontSize: 11,
            color: MUTED,
            marginTop: 8,
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 7,
            lineHeight: 1.5,
          }}
        >
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            taker cost at execution · {priced.toLocaleString()} priced
          </span>
          {m.single_source_reference ? (
            <div style={{ color: AMBER, marginTop: 3 }}>single-source reference</div>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={headRow}>
        <span style={{ fontSize: 12, color: MUTED }}>Execution cost vs reference</span>
        <span style={{ fontSize: 11, color: MUTED }}>{m?.window_days ?? 30}d</span>
      </div>
      {body}
    </div>
  );
}
